import { cn } from "@newxsystems/shared";
import { TootActions } from "../../../../modules/layout/components/TootActions/TootActions";
import { TootHeading } from "../../../../modules/layout/components/TootHeading/TootHeading";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import { TootMediaFrame } from "../../../../modules/surface/components/TootMediaFrame/TootMediaFrame";
import type { IMasonryGalleryBlockProps, MasonryGalleryItem } from "./MasonryGalleryBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

const getDimensionLabel = (item: MasonryGalleryItem) => {
  const width = item.width ?? item.image.width;
  const height = item.height ?? item.image.height;

  return width && height ? `${width}×${height}` : undefined;
};

export const MasonryGalleryBlock = ({
  anchorId,
  buttons,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing = "normal",
  subheading,
}: IMasonryGalleryBlockProps) => {
  if (!items?.length) {
    return null;
  }

  const alignment = "left" as const;
  const actions = buttons?.length ? (
    <TootActions actions={buttons} alignment={alignment} />
  ) : undefined;
  const hasHeaderContent = Boolean(eyebrow || heading || subheading || actions);
  const header = hasHeaderContent ? (
    heading ? (
      <TootHeading
        actions={actions}
        alignment={alignment}
        className="!max-w-none [&_.toot-heading-section]:!max-w-none [&_.toot-subheading]:!max-w-none"
        eyebrow={eyebrow}
        heading={heading}
        subheading={subheading}
      />
    ) : (
      <div
        className={cn(
          "flex !max-w-none flex-col gap-4 [&_.toot-subheading]:!max-w-none",
          "items-start text-left",
        )}
      >
        {eyebrow ? <p className="toot-eyebrow">{eyebrow}</p> : null}
        {subheading ? <RichTextHtml className="toot-subheading" html={subheading} /> : null}
        {actions ? <div className="pt-2">{actions}</div> : null}
      </div>
    )
  ) : undefined;
  return (
    <TootSectionShell
      className="relative"
      header={header}
      id={anchorId ?? undefined}
      rootClassName={parentClass}
      spacing={spacing ?? undefined}
      tone="default"
      width="wide"
    >
      <div className="relative">
        <div className="toot-heat-panel p-[clamp(0.7rem,1.8vw,1.15rem)]">
          <div className="relative z-[1] columns-1 [column-gap:clamp(0.85rem,1.6vw,1.25rem)] md:columns-2 xl:columns-3">
            {items.map((item, index) => {
              const image = item.image;
              const src = image.defaultSrc;
              const tags = (item.tags ?? []).map((tag) => tag.tag);
              const categoryLabel = item.category;
              const creditLabel = item.credit;
              const dimensionLabel = getDimensionLabel(item);
              const metadata = [creditLabel, dimensionLabel].filter((value): value is string =>
                Boolean(value),
              );

              return (
                <article
                  className="toot-heat-panel group/capture mb-[clamp(0.85rem,1.6vw,1.25rem)] inline-block w-full break-inside-avoid transition-[border-color,box-shadow,transform] duration-[260ms] ease-out hover:-translate-y-1 hover:border-toot-orange/60 hover:shadow-2xl hover:shadow-black/50 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                  key={`${src}-${index}`}
                >
                  {categoryLabel ? (
                    <div className="relative z-[1] grid min-h-[2.45rem] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-[0.65rem] border-b border-toot-grid-strong/75 bg-toot-surface-raised/75 px-3.5 pb-[0.55rem] pt-3 text-[0.58rem] font-black uppercase leading-[1.2] tracking-[0.16em] text-toot-muted">
                      <span className="min-w-0 truncate text-[var(--color-toot-orange)]">
                        {categoryLabel}
                      </span>
                    </div>
                  ) : null}

                  <div className="relative z-[1] overflow-hidden border-b border-toot-grid-strong/70">
                    <TootMediaFrame
                      alt={image.alternateText}
                      aspectRatio={item.aspectRatio ?? "4 / 5"}
                      className="toot-media-heat rounded-none border-0 [&>div]:min-h-56 [&_img]:transition-transform [&_img]:duration-500 [&_img]:ease-out group-hover/capture:[&_img]:scale-[1.035] motion-reduce:[&_img]:transition-none"
                      focalPoint={image.focalPoint ?? undefined}
                      height={item.height ?? image.height ?? undefined}
                      loading={index < 3 ? "eager" : "lazy"}
                      sizes={
                        image.sizes ?? "(min-width: 1280px) 30vw, (min-width: 768px) 45vw, 100vw"
                      }
                      src={src}
                      srcSet={image.srcSet ?? undefined}
                      width={item.width ?? image.width ?? undefined}
                    />
                  </div>

                  <div className="relative z-[1] flex flex-col gap-[0.85rem] p-4">
                    {item.caption ? (
                      <p className="m-0 text-pretty text-[0.98rem] md:text-[1.12rem] font-black leading-[1.15] tracking-[-0.025em] text-[var(--color-toot-foreground)] [overflow-wrap:anywhere]">
                        {item.caption}
                      </p>
                    ) : null}

                    {metadata.length > 0 ? (
                      <p className="m-0 flex flex-wrap gap-x-[0.85rem] gap-y-[0.35rem] text-[0.62rem] font-black uppercase leading-[1.35] tracking-[0.16em] text-toot-orange">
                        {metadata.map((value) => (
                          <span key={value}>{value}</span>
                        ))}
                      </p>
                    ) : null}

                    {tags.length > 0 ? (
                      <ul
                        aria-label="Capture tags"
                        className="m-0 flex list-none flex-wrap gap-[0.45rem] p-0"
                      >
                        {tags.map((tag, tagIndex) => (
                          <li
                            className="rounded-[var(--radius-toot-panel)] border border-toot-grid-strong/75 bg-toot-surface-raised/65 px-[0.55rem] py-[0.36rem] text-xs font-bold leading-none text-toot-muted"
                            key={`${tag}-${tagIndex}`}
                          >
                            {tag}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </TootSectionShell>
  );
};

MasonryGalleryBlock.displayName = "Block::MasonryGalleryBlock";
